import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import type { PatientListMetricsRow } from '../lib/types';
import { formatCents, formatDate, formatPhone } from '../lib/format';
import { humanizePatientListError } from '../lib/apiErrors';

const PAGE_SIZE = 25;

type SortKey = 'last_visit' | 'ltv' | 'ticket';

export default function Pacientes() {
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const source = params.get('source') ?? '';
  const lifecycle = params.get('lifecycle') ?? '';
  const sort = (params.get('sort') ?? 'last_visit') as SortKey;
  const page = Math.max(1, Number(params.get('page') ?? '1'));

  const [rows, setRows] = useState<PatientListMetricsRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorHint, setErrorHint] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    setErrorHint(null);
    (async () => {
      let query = supabase
        .from('patient_list_metrics')
        .select('*', { count: 'exact' })
        .range((page - 1) * PAGE_SIZE, page * PAGE_SIZE - 1);

      if (sort === 'last_visit') {
        query = query.order('vissmed_last_visit_at', { ascending: false, nullsFirst: false });
      } else if (sort === 'ltv') {
        query = query.order('vissmed_total_spent_cents', { ascending: false, nullsFirst: false });
      } else {
        query = query.order('ticket_medio_cents', { ascending: false, nullsFirst: false });
      }
      query = query.order('id', { ascending: false });

      if (q.trim()) {
        const raw = q.trim().replace(/"/g, '');
        const digits = raw.replace(/\D/g, '');
        const pat = `%${raw}%`;
        if (digits.length >= 8) {
          query = query.or(`name.ilike."${pat}",phone_digits.ilike."%${digits}%",email.ilike."${pat}"`);
        } else {
          query = query.or(`name.ilike."${pat}",email.ilike."${pat}"`);
        }
      }
      if (source) query = query.eq('first_seen_source', source);
      if (lifecycle) query = query.eq('lifecycle_stage', lifecycle);

      const { data, count, error: e } = await query;
      if (e) {
        const { message, hint } = humanizePatientListError(e.message);
        setError(message);
        setErrorHint(hint ?? null);
      }
      setRows((data ?? []) as PatientListMetricsRow[]);
      setTotal(count ?? 0);
      setLoading(false);
    })();
  }, [q, source, lifecycle, sort, page]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / PAGE_SIZE)), [total]);

  function setParam(key: string, value: string) {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key !== 'page') next.delete('page');
    setParams(next);
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold">Pacientes</h1>
        <div className="text-sm text-slate-500">{total.toLocaleString('pt-BR')} total</div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-4 mb-4 grid grid-cols-1 md:grid-cols-5 gap-3">
        <input
          type="search"
          placeholder="Buscar nome, telefone ou e-mail…"
          defaultValue={q}
          onKeyDown={(e) => {
            if (e.key === 'Enter') setParam('q', (e.target as HTMLInputElement).value);
          }}
          className="md:col-span-2 px-3 py-2 border border-slate-300 rounded-md text-sm"
        />
        <select
          value={source}
          onChange={(e) => setParam('source', e.target.value)}
          className="px-3 py-2 border border-slate-300 rounded-md text-sm"
        >
          <option value="">Origem (todas)</option>
          <option value="meta">Meta</option>
          <option value="google">Google</option>
          <option value="organic">Orgânico</option>
          <option value="referral">Indicação</option>
        </select>
        <select
          value={lifecycle}
          onChange={(e) => setParam('lifecycle', e.target.value)}
          className="px-3 py-2 border border-slate-300 rounded-md text-sm"
        >
          <option value="">Estágio (todos)</option>
          <option value="new">Novo</option>
          <option value="lead">Lead</option>
          <option value="qualified">Qualificado</option>
          <option value="scheduled">Agendado</option>
          <option value="attended">Atendido</option>
          <option value="lost">Perdido</option>
        </select>
        <select
          value={sort}
          onChange={(e) => setParam('sort', e.target.value)}
          className="px-3 py-2 border border-slate-300 rounded-md text-sm"
        >
          <option value="last_visit">Ordenar: última visita (Vissmed)</option>
          <option value="ltv">Ordenar: LTV total</option>
          <option value="ticket">Ordenar: ticket médio</option>
        </select>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-md p-3 text-sm mb-4">
          <div>{error}</div>
          {errorHint && (
            <div className="mt-2 text-xs text-red-600/90">{errorHint}</div>
          )}
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[960px]">
          <thead className="bg-slate-50 text-slate-600 text-left text-xs uppercase tracking-wide">
            <tr>
              <th className="px-3 py-3">Nome</th>
              <th className="px-3 py-3">Telefone</th>
              <th className="px-3 py-3">Última especialidade</th>
              <th className="px-3 py-3 text-right">Ticket médio</th>
              <th className="px-3 py-3 text-right">LTV total</th>
              <th className="px-3 py-3">1ª consulta</th>
              <th className="px-3 py-3">Última visita</th>
              <th className="px-3 py-3">Estágio</th>
              <th className="px-3 py-3">Origem</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && (
              <tr>
                <td colSpan={9} className="px-4 py-8 text-center text-slate-400">
                  Carregando…
                </td>
              </tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={9} className="px-4 py-8 text-center text-slate-400">
                  Nenhum paciente encontrado.
                </td>
              </tr>
            )}
            {rows.map((p) => (
              <tr key={p.id} className="hover:bg-slate-50">
                <td className="px-3 py-2">
                  <Link to={`/pacientes/${p.id}`} className="text-brand-700 hover:underline font-medium">
                    {p.name ?? '(sem nome)'}
                  </Link>
                </td>
                <td className="px-3 py-2 text-slate-600">{formatPhone(p.phone_e164)}</td>
                <td className="px-3 py-2 text-slate-700 text-xs max-w-[200px] truncate" title={p.vissmed_last_specialty ?? ''}>
                  {p.vissmed_last_specialty ?? '—'}
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-green-700 font-medium">
                  {formatCents(p.ticket_medio_cents)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-slate-800">
                  {formatCents(p.vissmed_total_spent_cents)}
                </td>
                <td className="px-3 py-2 text-slate-600">{formatDate(p.vissmed_first_visit_at)}</td>
                <td className="px-3 py-2 text-slate-600">{formatDate(p.vissmed_last_visit_at)}</td>
                <td className="px-3 py-2">
                  <LifecyclePill stage={p.lifecycle_stage} />
                </td>
                <td className="px-3 py-2 text-slate-600 text-xs">{p.first_seen_source ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-end gap-2 mt-4 text-sm">
          <button
            disabled={page <= 1}
            onClick={() => setParam('page', String(page - 1))}
            className="px-3 py-1.5 rounded-md border border-slate-300 disabled:opacity-40"
          >
            Anterior
          </button>
          <span className="text-slate-600">
            Página {page} de {totalPages}
          </span>
          <button
            disabled={page >= totalPages}
            onClick={() => setParam('page', String(page + 1))}
            className="px-3 py-1.5 rounded-md border border-slate-300 disabled:opacity-40"
          >
            Próxima
          </button>
        </div>
      )}
    </div>
  );
}

function LifecyclePill({ stage }: { stage: string | null }) {
  const map: Record<string, string> = {
    new: 'bg-slate-100 text-slate-700',
    lead: 'bg-slate-100 text-slate-700',
    qualified: 'bg-blue-100 text-blue-700',
    scheduled: 'bg-amber-100 text-amber-700',
    attended: 'bg-emerald-100 text-emerald-700',
    lost: 'bg-red-100 text-red-700',
    archived: 'bg-slate-100 text-slate-500',
  };
  const cls = map[stage ?? ''] ?? 'bg-slate-100 text-slate-500';
  return (
    <span className={`inline-block text-xs px-2 py-0.5 rounded-full ${cls}`}>{stage ?? '—'}</span>
  );
}
